// Pure per-individual, per-disease carrier-status projection.
//
// Inputs are pre-normalised by the caller (Canvas integration) so this stays
// free of d3 / React / network concerns and is trivially testable:
//
//   members[]            { pid, diagnoses[{id,displayOnPedigree}],
//                          genotypes[{symbol,carrierStatus}],
//                          obligateCarrierAssertions[{diseaseId}] }
//   familyDiseases[]     { id, externalIds:['OMIM:114480', ...] }
//   geneDiseaseMap       { [geneSymbol]: ['OMIM:114480', ...] }  (terminology)
//
// Output: { byMember: { pid: { diseaseId: status } },
//           presentStatesByDisease: { diseaseId: Set<'affected'|'carrier'> } }

import {
  CARRIER_STATUS,
  TESTED_CARRIER_VALUES,
  PRESENT_STATE
} from './constants';

// Sources whose codes never take the source-agnostic token. An SCTID is 6 to
// 18 digits, so '*:<digits>' could read a short one as the OMIM or Orphanet
// number with the same digits; a SNOMED CT code matches as itself only
// (TGCLOUD-1847). Its OMIM or Orphanet twin reaches it through the gene map,
// which carries each association's SNOMED CT equivalents by Mondo.
const QUALIFIED_ONLY = new Set(['SCTID']);

// Normalise an external disease id into matchable tokens. Produces a
// source-qualified token ('OMIM:114480') and a source-agnostic one ('*:114480')
// so a bare family alias still matches a source-prefixed association.
export function tokensFor(externalId) {
  if (externalId == null) return [];
  const raw = String(externalId).trim();
  if (!raw) return [];
  const colon = raw.indexOf(':');
  if (colon < 1) {
    const bare = raw.replace(/^#/, '');
    return bare ? [`*:${bare}`] : [];
  }
  let source = raw.slice(0, colon).trim().toUpperCase();
  if (source === 'ORPHANET') source = 'ORPHA';
  // SNOMED CT as exports and Mondo spell it.
  if (['SNOMEDCT', 'SNOMEDCT_US', 'SNOMED', 'SNOMED CT'].includes(source)) {
    source = 'SCTID';
  }
  const code = raw
    .slice(colon + 1)
    .trim()
    .replace(/^#/, '');
  if (!code) return [];
  if (QUALIFIED_ONLY.has(source)) return [`${source}:${code}`];
  return [`${source}:${code}`, `*:${code}`];
}

export function deriveCarrierStatus({
  members = [],
  familyDiseases = [],
  geneDiseaseMap = {},
  // FEATURE_CARRIER_STATUS_IN_CONDITION. When false (default) this function is
  // byte-identical to its pre-flag behaviour. When true, an explicit
  // per-condition affectedStatus/carrierBasis on a diagnosis is authoritative
  // for that disease (overlay applied after the legacy derivation below).
  conditionCarrierEnabled = false
} = {}) {
  // token → familyDiseaseId
  const tokenToDiseaseId = new Map();
  for (const d of familyDiseases) {
    if (!d || d.id == null) continue;
    for (const ext of d.externalIds || []) {
      for (const tok of tokensFor(ext)) {
        if (!tokenToDiseaseId.has(tok)) tokenToDiseaseId.set(tok, d.id);
      }
    }
  }

  const familyDiseaseIdsForGene = (geneSymbol) => {
    const out = new Set();
    for (const ext of geneDiseaseMap[geneSymbol] || []) {
      for (const tok of tokensFor(ext)) {
        const id = tokenToDiseaseId.get(tok);
        if (id != null) out.add(id);
      }
    }
    return out;
  };

  const byMember = {};
  const presentStatesByDisease = {};
  const addPresent = (diseaseId, state) => {
    (
      presentStatesByDisease[diseaseId] ||
      (presentStatesByDisease[diseaseId] = new Set())
    ).add(state);
  };

  for (const m of members) {
    if (!m || m.pid == null) continue;
    const statuses = {};

    // affected — from existing diagnosis display flag, always wins.
    for (const dx of m.diagnoses || []) {
      if (dx && dx.displayOnPedigree && dx.id != null) {
        statuses[dx.id] = CARRIER_STATUS.affected;
      }
    }

    // tested_carrier — per-gene assertion projected through the gene→disease map.
    for (const g of m.genotypes || []) {
      const symbol = (g && (g.symbol || g.gene)) || '';
      const sym = String(symbol).trim();
      if (!sym) continue;
      const status = String((g && g.carrierStatus) || '').trim();
      if (!TESTED_CARRIER_VALUES.has(status)) continue;
      for (const diseaseId of familyDiseaseIdsForGene(sym)) {
        if (statuses[diseaseId] === CARRIER_STATUS.affected) continue;
        statuses[diseaseId] = CARRIER_STATUS.testedCarrier;
      }
    }

    // obligate_carrier — legacy manual assertion; only when not affected and not
    // tested. Gated behind FEATURE_CARRIER_STATUS_IN_CONDITION: all obligate-
    // carrier handling is hidden until the toggle is on (pending clinical
    // review), so flag-off produces no obligate_carrier from legacy data.
    if (conditionCarrierEnabled) {
      for (const a of m.obligateCarrierAssertions || []) {
        const diseaseId = a && a.diseaseId;
        if (diseaseId == null) continue;
        const current = statuses[diseaseId];
        if (
          current === CARRIER_STATUS.affected ||
          current === CARRIER_STATUS.testedCarrier
        )
          continue;
        statuses[diseaseId] = CARRIER_STATUS.obligateCarrier;
      }
    }

    // Per-condition overlay (FEATURE_CARRIER_STATUS_IN_CONDITION). An explicit
    // affectedStatus on a diagnosis is the clinician's authoritative statement
    // for that disease and overrides the derivations above — gated by
    // displayOnPedigree (the per-condition "show on pedigree" toggle). obligate
    // vs tested follows carrierBasis (both render as the same carrier mark).
    // A diagnosis with NO affectedStatus is left untouched, so data predating
    // the flag is unaffected.
    if (conditionCarrierEnabled) {
      for (const dx of m.diagnoses || []) {
        if (!dx || dx.id == null || !dx.affectedStatus) continue;
        if (dx.affectedStatus === 'affected') {
          if (dx.displayOnPedigree) statuses[dx.id] = CARRIER_STATUS.affected;
        } else if (dx.affectedStatus === 'carrier') {
          if (dx.displayOnPedigree) {
            const basis = dx.carrierBasis || {};
            // Basis precedence (all render the same mark; this only sets the
            // status label): tested > reported > obligate > generic carrier.
            // A carrier with no stated basis is a neutral 'carrier', never
            // silently obligate.
            statuses[dx.id] = basis.tested
              ? CARRIER_STATUS.testedCarrier
              : basis.reported
              ? CARRIER_STATUS.reportedCarrier
              : basis.obligate
              ? CARRIER_STATUS.obligateCarrier
              : CARRIER_STATUS.carrier;
          }
        } else if (dx.displayOnPedigree) {
          // 'unaffected' | 'unknown' — explicit non-mark. Gated by
          // displayOnPedigree like the other statuses: a hidden condition is
          // inert and never suppresses an independent (e.g. genotype-derived)
          // mark for the same disease.
          delete statuses[dx.id];
        }
      }
    }

    byMember[m.pid] = statuses;

    // Present-states derived from the FINAL per-member statuses so the overlay's
    // upgrades / downgrades / clears are reflected. With the flag off this is
    // identical to accumulating inline (affected→'affected', else 'carrier').
    for (const diseaseId of Object.keys(statuses)) {
      addPresent(
        diseaseId,
        statuses[diseaseId] === CARRIER_STATUS.affected
          ? PRESENT_STATE.affected
          : PRESENT_STATE.carrier
      );
    }
  }

  return { byMember, presentStatesByDisease };
}
